import { useMemo, useEffect, useCallback } from "react";
import { FOCUS_CONFIG, FOCUS_BORDER } from "../../config/focusStyles";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LazyRow from "../common/LazyRow";

// ============================================================================
// CONSTANTS
// ============================================================================

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

var ContentRowWrapper = styled.div`
  position: relative;
`;

var RowContainer = styled.div`
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  width: 100%;
`;

var ItemWrapper = styled.div`
  width: 20%;
  min-width: 140px;
  padding: 12px;
  padding-top: 24px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: relative;
  -webkit-box-flex: 0;
  -ms-flex-positive: 0;
  flex-grow: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;
`;

var SeriesDecorationWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 12px;
  right: 12px;
  height: 24px;
  overflow: hidden;
`;

var SeriesImage = styled.img`
  width: 100%;
  height: auto;
`;

var CardOuter = styled.div`
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
`;

var CardContainer = styled.div`
  width: 100%;
  height: 0;
  padding-top: 140%;
  position: relative;
  cursor: pointer;
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
`;

var ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`;

var FocusBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px;
  border: ${function (props) {
    return props.$focused
      ? FOCUS_CONFIG.width + " solid " + FOCUS_CONFIG.color
      : FOCUS_CONFIG.width + " solid transparent";
  }};
  pointer-events: none;
  z-index: 20;
`;

var FocusOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${function (props) { return props.$focused ? 1 : 0; }};
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  gap: 1rem;
  z-index: 10;
`;

var Title = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: "AnjomanMax", serif;
`;

var Year = styled.span`
  color: white;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  font-family: "AnjomanMax", serif;
`;

// ============================================================================
// COMPONENTS
// ============================================================================

var MovieItem = function (props) {
  var item = props.item || {};
  var onEnterPress = props.onEnterPress;

  var contentPhotos = item.contentPhotos;
  var tags = item.tags;
  var contentType = item.contentType;
  var _id = item._id;
  var kind = item.kind;

  var focusableResult = useFocusable({
    onEnterPress: onEnterPress,
    focusKey: "search_content_" + _id,
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  var isSeries = contentType?.kind === "series" || kind === "series";

  // Get optimized image with LQIP and WebP support (w=200, q=95)
  var imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 95, format: "jpeg" });

  return (
    <ItemWrapper>
      {isSeries && (
        <SeriesDecorationWrapper>
          <SeriesImage src="/serial_pseudo.svg" alt="" />
        </SeriesDecorationWrapper>
      )}
      <CardOuter
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onClick={onEnterPress}
      >
        <CardContainer>
          <ImageContainer>
            <OptimizedImage
              src={imageData.src}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </ImageContainer>
          {tags?.[0] && <ContentTag label={tags[0]} />}
          <FocusOverlay $focused={focused}>
            <Title>{item.title}</Title>
            <Year>{item.year}</Year>
          </FocusOverlay>
        </CardContainer>
        <FocusBorder $focused={focused} />
      </CardOuter>
    </ItemWrapper>
  );
};

/**
 * Single row of movie items with its own focus context
 */
var MovieRow = function (props) {
  var items = props.items;
  var onFocus = props.onFocus;
  var navigate = props.navigate;

  var focusableResult = useFocusable({
    onFocus,
  });
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  return (
    <FocusContext.Provider value={focusKey}>
      <RowContainer ref={ref}>
        {items.map(function (item, index) {
          var contentKind = item.contentType?.kind || "movie";
          return (
            <MovieItem
              item={item}
              key={item._id + "_" + index}
              onEnterPress={function () {
                navigate("/" + contentKind + "/" + item.slug);
              }}
            />
          );
        })}
      </RowContainer>
    </FocusContext.Provider>
  );
};

export var MoviesSlider = function (props) {
  var movies = props.movies;
  var onFocus = props.onFocus;
  var shouldFocusSelf = props.shouldFocusSelf;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return (
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
    );
  });

  var focusableResult = useFocusable();
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;
  var focusSelf = focusableResult.focusSelf;

  var navigate = useNavigate();

  useEffect(
    function () {
      if (shouldFocusSelf === true) {
        focusSelf();
      }
    },
    [shouldFocusSelf, focusSelf]
  );

  // Chunk items into rows
  var chunkedItems = useMemo(
    function () {
      if (!_items || _items.length === 0) return [];
      return chunkArray(_items, ITEMS_PER_ROW);
    },
    [_items]
  );

  if (!_items || _items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref}>
        {chunkedItems.map(function (rowItems, rowIndex) {
          return (
            <LazyRow key={"search_row_" + rowIndex} index={rowIndex}>
              <MovieRow
                items={rowItems}
                onFocus={onFocus}
                navigate={navigate}
              />
            </LazyRow>
          );
        })}
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
